/**
 * Render an item/supply count.
 *
 * The backend's `totalSupply` is authoritative but two shapes need care:
 *  - it arrives as a decimal string (it overflows JS number for big supplies),
 *  - ERC-404 collections report a sentinel max-uint64-ish value (e.g.
 *    18446744073709552000) that is a placeholder, not a real count.
 * Absent or sentinel values render as an em dash rather than a fake number.
 */
function formatCount(value?: number | string | bigint | null): string {
  if (value === undefined || value === null) return '—';
  let n: bigint;
  try {
    n = BigInt(typeof value === 'string' ? value.trim() : value);
  } catch {
    return '—';
  }
  if (n <= 0n) return '—';
  // ERC-404 sentinel: any supply above 1e18 is not a real minted count.
  if (n >= 10n ** 18n) return '—';
  return n.toString();
}

/**
 * BreadcrumbNoMask — web3-style breadcrumb without background mask
 */
import ContractAddress from '../ContractAddress';

function BreadcrumbNoMask(
  { text1, text2, text3, net, image, colldesc, socials, items, chainId, numHolders }: {
    text1: string;
    text2?: string | null;
    text3?: string;
    net?: { png: string; alt: string } | null;
    image?: string;
    colldesc?: string;
    socials?: object;
    /** Live item count for the collection. Rendered as-is when supplied. */
    items?: number | string | null;
    /** Distinct owner count for the collection. Renders em dash if missing. */
    numHolders?: number | string | bigint | null;
    /** Chain id for the contract address' explorer link. Falls back to net.alt. */
    chainId?: string | number | null
  }
) {
  // `net.alt` is the chain id on this page, so it is a usable fallback when the
  // caller doesn't pass chainId explicitly.
  const addrChainId = chainId ?? net?.alt ?? null;
  return (
    <div className="breadcrumb-nomask" style={{ display: 'flex', alignItems: 'flex-start', gap: '0rem' }}>
      {image && (
        <div style={{ width: '16.6667%', aspectRatio: '1 / 1', flexShrink: 0, overflow: 'hidden', borderRadius: 8, border: '2px solid #59ffae' }}>
          <img
            src={image}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
            }}
          />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0, paddingLeft: 20 }}>
        <h2 className="breadcrumb-nomask-title" style={{ margin: 0 }}>{text3}</h2>
        <h4 className="text-green" style={{ paddingLeft: 5, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {text1}
          {/* Contract address sits after the symbol, on the same line. */}
          {text2 ? <ContractAddress address={text2} chainId={addrChainId} /> : null}
        </h4>
          <h6 className="text-white">{colldesc}</h6>
      </div>
      <div className="breadcrumb-cards-col">
        <div className="pixel-card" style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center', padding: '1rem' }}>
          <div className="breadcrumb-stat">
            <span className="breadcrumb-stat-value">{formatCount(items)}</span>
            <span className="breadcrumb-stat-label">Items</span>
          </div>
          <div className="breadcrumb-stat">
            <span className="breadcrumb-stat-value">{formatCount(numHolders)}</span>
            <span className="breadcrumb-stat-label">Owners</span>
          </div>
          <div className="breadcrumb-stat">
            <span className="breadcrumb-stat-value">12 FLR</span>
            <span className="breadcrumb-stat-label">Floor Price</span>
          </div>
          <div className="breadcrumb-stat">
            <span className="breadcrumb-stat-value">2,481 FLR</span>
            <span className="breadcrumb-stat-label">Total Volume</span>
          </div>
        </div>
        <div className="pixel-card breadcrumb-chain-card">
          <div style={{ margin: 0, color: '#aeb6c8' }}>{net ? (
              <div className="breadcrumb-nomask-badge" style={{ flexShrink: 0 }}>
                  <img src={'/assets/img/chain-logos/chain-'+net.alt+'.png'} alt={net.alt} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
              </div>
          ) : null}</div>
        </div>
      </div>

    </div>
  );
}

export default BreadcrumbNoMask;
